import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./TelealarmeISSQuote.css";

/**
 * Téléalarme ISS — 08 / Demande de devis.
 * Formulaire React raccordable à un endpoint réel.
 * Aucun message de succès sans réponse positive du serveur.
 *
 * Configuration possible :
 *  - window.__UNITECH_ISS_QUOTE_ENDPOINT__ = "/api/iss/devis";
 *  - ou REACT_APP_ISS_QUOTE_ENDPOINT dans un projet Create React App.
 * Ne jamais placer d'identifiants privés ou de clés API dans le frontend.
 */
const COPY = {
  fr: {
    eyebrow: "PARLONS DE VOTRE INSTALLATION",
    title: "Étudions votre projet de téléalarme.",
    description:
      "Un appareil à moderniser ou un parc à équiper ? Présentez-nous votre besoin pour identifier une configuration adaptée.",
    benefits: [
      "Échange avec un interlocuteur technique",
      "Analyse préalable de votre configuration",
      "Proposition adaptée au périmètre retenu"
    ],
    formTitle: "Demander un devis",
    formIntro: "Décrivez votre installation : notre équipe pourra étudier votre demande.",
    setupNotice: "Formulaire de test : le service de réception des devis n’est pas encore configuré.",
    labels: {
      name: "Nom",
      company: "Entreprise",
      email: "Email professionnel",
      installation: "Type d'installation",
      quantity: "Nombre d'appareils",
      message: "Votre projet"
    },
    placeholders: {
      name: "Votre nom",
      company: "Votre entreprise",
      email: "nom@entreprise.fr",
      installation: "Sélectionnez une situation",
      quantity: "Facultatif",
      message: "Décrivez brièvement votre besoin…"
    },
    options: [
      ["existing", "Installation existante"],
      ["new", "Installation neuve"],
      ["modernization", "Modernisation / remplacement"],
      ["fleet", "Parc de plusieurs appareils"],
      ["other", "Autre situation"]
    ],
    optional: "facultatif",
    privacy: "J'accepte d'être recontacté au sujet de cette demande de devis.",
    privacyNote:
      "Les informations demandées servent à étudier votre projet et à vous recontacter à ce sujet.",
    submit: "Demander un devis",
    submitting: "Envoi en cours…",
    question: "Une question technique ?",
    expert: "Parler à un expert",
    note: "Une étude technique permet de confirmer la compatibilité selon l'installation.",
    unavailable:
      "L'envoi en ligne n'est pas encore raccordé. Pour nous joindre, utilisez la page Contact.",
    invalid: "Vérifiez les informations saisies avant de poursuivre.",
    invalidEmail: "Veuillez saisir une adresse email valide.",
    invalidQuantity: "Le nombre d'appareils doit être un entier positif.",
    error:
      "Votre demande n'a pas été envoyée. Réessayez ou contactez-nous directement.",
    timeout:
      "Le serveur n'a pas répondu à temps. Aucune confirmation d'envoi n'a été reçue.",
    success:
      "Votre demande de devis a bien été transmise. Merci, notre équipe pourra l'étudier."
  },
  en: {
    eyebrow: "LET'S DISCUSS YOUR INSTALLATION",
    title: "Let's review your emergency call project.",
    description:
      "One elevator to modernise or a whole fleet to equip? Tell us about your needs so we can assess a suitable configuration.",
    benefits: [
      "Discussion with a technical specialist",
      "Initial review of your configuration",
      "Proposal tailored to the agreed scope"
    ],
    formTitle: "Request a quote",
    formIntro: "Describe your installation so our team can review your request.",
    setupNotice: "Test form: the quote reception service is not yet configured.",
    labels: {
      name: "Name",
      company: "Company",
      email: "Business email",
      installation: "Installation type",
      quantity: "Number of elevators",
      message: "Your project"
    },
    placeholders: {
      name: "Your name",
      company: "Your company",
      email: "name@company.com",
      installation: "Choose a situation",
      quantity: "Optional",
      message: "Briefly describe your needs…"
    },
    options: [
      ["existing", "Existing installation"],
      ["new", "New installation"],
      ["modernization", "Modernisation / replacement"],
      ["fleet", "Multiple elevators"],
      ["other", "Other situation"]
    ],
    optional: "optional",
    privacy: "I agree to be contacted regarding this quotation request.",
    privacyNote:
      "The information requested is used to assess your project and contact you about it.",
    submit: "Request a quote",
    submitting: "Sending…",
    question: "Have a technical question?",
    expert: "Talk to an expert",
    note: "A technical assessment is required to confirm compatibility for your installation.",
    unavailable:
      "Online submission has not been connected yet. Please use the Contact page to reach us.",
    invalid: "Please check the information before continuing.",
    invalidEmail: "Please enter a valid email address.",
    invalidQuantity: "The number of elevators must be a positive integer.",
    error: "Your request was not sent. Please try again or contact us directly.",
    timeout: "The server did not respond in time. Submission could not be confirmed.",
    success: "Your quotation request has been sent. Thank you — our team can now review it."
  }
};

const INITIAL_FORM = {
  name: "",
  company: "",
  email: "",
  installation: "",
  quantity: "",
  message: "",
  privacy: false,
  website: "" // Honeypot : le serveur doit également vérifier ce champ.
};

function getEndpoint() {
  const runtimeEndpoint =
    typeof window !== "undefined" &&
    typeof window.__UNITECH_ISS_QUOTE_ENDPOINT__ === "string"
      ? window.__UNITECH_ISS_QUOTE_ENDPOINT__.trim()
      : "";
  const buildEndpoint =
    typeof process !== "undefined" && process.env &&
    typeof process.env.REACT_APP_ISS_QUOTE_ENDPOINT === "string"
      ? process.env.REACT_APP_ISS_QUOTE_ENDPOINT.trim()
      : "";
  const endpoint = runtimeEndpoint || buildEndpoint;

  // Same-origin path or HTTPS URL only. Prevent accidental insecure submissions.
  if ((endpoint.startsWith("/") && !endpoint.startsWith("//")) ||
      endpoint.startsWith("https://")) {
    return endpoint;
  }
  return "";
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" fill="none" aria-hidden="true">
      <path d="m4 10 4 4 8-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" width="19" height="19" fill="none" aria-hidden="true">
      <path d="M5 12h14m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function TelealarmeISSQuote() {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const copy = COPY[language] || COPY.fr;
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState({ type: "idle", message: "" });
  const isSending = status.type === "sending";
  const formRef = useRef(null);

  function updateField(event) {
    const { name, value, type, checked } = event.target;
    setForm((previous) => ({ ...previous, [name]: type === "checkbox" ? checked : value }));
    if (status.type !== "idle" && status.type !== "sending") {
      setStatus({ type: "idle", message: "" });
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (isSending) return;

    if (!formRef.current?.reportValidity()) {
      setStatus({ type: "error", message: copy.invalid });
      return;
    }

    const name = form.name.trim();
    const company = form.company.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    const count = form.quantity.trim();
    const quantityIsValid = !count || (/^[0-9]+$/.test(count) && Number(count) > 0 && Number(count) <= 1000000);

    if (!name || !company || !message || !form.installation || !form.privacy) {
      setStatus({ type: "error", message: copy.invalid });
      return;
    }
    if (!emailPattern.test(email)) {
      setStatus({ type: "error", message: copy.invalidEmail });
      return;
    }
    if (!quantityIsValid) {
      setStatus({ type: "error", message: copy.invalidQuantity });
      return;
    }

    const endpoint = getEndpoint();
    if (!endpoint) {
      setStatus({ type: "unavailable", message: copy.unavailable });
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    setStatus({ type: "sending", message: "" });

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        signal: controller.signal,
        body: JSON.stringify({
          product: "Telealarme ISS",
          source: "telealarme-iss-quote-form",
          language,
          name,
          company,
          email,
          installationType: form.installation,
          equipmentCount: count ? Number(count) : null,
          message,
          contactRequested: true,
          website: form.website
        })
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || result?.success === false || result?.ok === false) {
        throw new Error("Server did not confirm acceptance");
      }
      setForm(INITIAL_FORM);
      setStatus({ type: "success", message: copy.success });
    } catch (error) {
      setStatus({
        type: "error",
        message: error.name === "AbortError" ? copy.timeout : copy.error
      });
    } finally {
      clearTimeout(timer);
    }
  }

  return (
    <section className="iss-quote" id="iss-quote" aria-labelledby="iss-quote-title">
      <div className="iss-quote__container">
        <div className="iss-quote__copy">
          <p className="iss-quote__eyebrow">{copy.eyebrow}</p>
          <h2 className="iss-quote__title" id="iss-quote-title">{copy.title}</h2>
          <p className="iss-quote__description">{copy.description}</p>
          <ul className="iss-quote__benefits">
            {copy.benefits.map((benefit) => (
              <li key={benefit}>
                <CheckIcon />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
          <p className="iss-quote__context">{copy.note}</p>
        </div>

        <div className="iss-quote__panel">
          <h3 className="iss-quote__formTitle">{copy.formTitle}</h3>
          <p className="iss-quote__formIntro">{copy.formIntro}</p>
          {!getEndpoint() && <p className="iss-quote__setupNotice">{copy.setupNotice}</p>}

          <form className="iss-quote__form" ref={formRef} onSubmit={handleSubmit} noValidate={false}>
            <div className="iss-quote__fields iss-quote__fields--two">
              <div className="iss-quote__field">
                <label htmlFor="iss-quote-name">{copy.labels.name} <span aria-hidden="true">*</span></label>
                <input id="iss-quote-name" name="name" type="text" autoComplete="name" maxLength="100" required value={form.name} placeholder={copy.placeholders.name} onChange={updateField} disabled={isSending} />
              </div>
              <div className="iss-quote__field">
                <label htmlFor="iss-quote-company">{copy.labels.company} <span aria-hidden="true">*</span></label>
                <input id="iss-quote-company" name="company" type="text" autoComplete="organization" maxLength="120" required value={form.company} placeholder={copy.placeholders.company} onChange={updateField} disabled={isSending} />
              </div>
            </div>

            <div className="iss-quote__field">
              <label htmlFor="iss-quote-email">{copy.labels.email} <span aria-hidden="true">*</span></label>
              <input id="iss-quote-email" name="email" type="email" autoComplete="email" inputMode="email" maxLength="180" required value={form.email} placeholder={copy.placeholders.email} onChange={updateField} disabled={isSending} />
            </div>

            <div className="iss-quote__fields iss-quote__fields--two">
              <div className="iss-quote__field">
                <label htmlFor="iss-quote-installation">{copy.labels.installation} <span aria-hidden="true">*</span></label>
                <select id="iss-quote-installation" name="installation" required value={form.installation} onChange={updateField} disabled={isSending}>
                  <option value="" disabled>{copy.placeholders.installation}</option>
                  {copy.options.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="iss-quote__field">
                <label htmlFor="iss-quote-quantity">{copy.labels.quantity} <small>({copy.optional})</small></label>
                <input id="iss-quote-quantity" name="quantity" type="number" min="1" max="1000000" step="1" inputMode="numeric" value={form.quantity} placeholder={copy.placeholders.quantity} onChange={updateField} disabled={isSending} />
              </div>
            </div>

            <div className="iss-quote__field">
              <label htmlFor="iss-quote-message">{copy.labels.message} <span aria-hidden="true">*</span></label>
              <textarea id="iss-quote-message" name="message" rows="4" minLength="10" maxLength="2000" required value={form.message} placeholder={copy.placeholders.message} onChange={updateField} disabled={isSending} />
            </div>

            <div className="iss-quote__honeypot" aria-hidden="true">
              <label htmlFor="iss-quote-website">Website</label>
              <input id="iss-quote-website" name="website" type="text" tabIndex="-1" autoComplete="off" value={form.website} onChange={updateField} />
            </div>

            <label className="iss-quote__privacy">
              <input name="privacy" type="checkbox" checked={form.privacy} onChange={updateField} disabled={isSending} required />
              <span>{copy.privacy}</span>
            </label>
            <p className="iss-quote__privacyNote">{copy.privacyNote}</p>

            <button className="iss-quote__submit" type="submit" disabled={isSending}>
              <span>{isSending ? copy.submitting : copy.submit}</span>
              {!isSending && <ArrowIcon />}
            </button>

            {status.type !== "idle" && status.type !== "sending" && (
              <p className={`iss-quote__feedback iss-quote__feedback--${status.type}`} role={status.type === "success" ? "status" : "alert"} aria-live={status.type === "success" ? "polite" : "assertive"}>
                {status.message}
              </p>
            )}
            <p className="iss-quote__expert">
              {copy.question} <a href="/contact">{copy.expert} <span aria-hidden="true">↗</span></a>
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
